/**
 * CJdropshipping Alternatives 主页面入口
 * 路由：/alternatives
 * pageName：alternatives
 *
 * 页面结构：
 *   AlternativesHero  — Hero Banner（双列，右侧图片+浮动卡片）
 *   SellpointsBar     — 服务卖点横排标签（公共）
 *   CompareSection    — 5张 CJ vs 竞品对比卡片
 *   FitSection        — Who Should Choose（公共）
 *   AdvantagesSection — 核心优势 8 张卡片（公共）
 *   TestimonialSection — 双行跑马灯（公共）
 *   MainFaqSection    — 主页专属 FAQ（左图+info-grid+accordion）
 *   BottomCta         — 底部 CTA（公共）
 *
 * 【注意】写法遵循 odm/index.jsx 模式：
 *   - CdnHostContext + ConstantsContext 在根组件 provide
 *   - useMemo(() => getAlternativesConstants(), []) 缓存常量
 *   - CTA 按钮登录态处理：未登录→/login.html，已登录 PC→目标页
 *   - withSSR HOC 包装导出
 */

import React, { useState, useCallback, useContext, useMemo } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import styles from './index.less';
import { getAlternativesConstants } from './constants';
import {
  CdnHostContext,
  ConstantsContext,
  handleCtaClick,
  SellpointsBar,
  FitSection,
  AdvantagesSection,
  TestimonialSection,
  BottomCta,
  Kicker,
} from './shared/SharedComponents';

// ========== AlternativesHero ==========

/**
 * 主页 Hero Banner
 * 双列布局：左侧文案 + 右侧图片卡片（stat-card + float-card）
 * 背景：CSS radial-gradient（无需 CDN 图片）
 * 浮动装饰球：CSS animation: altFloat
 * CTA 按钮：点击跳 #explore-cj-service 锚点（页面内滚动）
 */
function AlternativesHero({ onCtaClick }) {
  const { HERO } = useContext(ConstantsContext);
  if (!HERO) return null;
  return (
    <section className={styles['hero']}>
      <div className={styles['container'] + ' ' + styles['hero-grid']}>
        {/* 左侧文案 */}
        <div>
          <Kicker text={HERO.kicker} styles={styles} />
          <h1
            className={styles['hero-title']}
            dangerouslySetInnerHTML={{ __html: HERO.title }}
          />
          <p className={styles['hero-desc']}>{HERO.desc}</p>
          <div className={styles['hero-actions']}>
            <button
              className={styles['btn']}
              id="get-started"
              onClick={function () {
                if (onCtaClick) onCtaClick(HERO.ctaUrl);
              }}
            >
              {HERO.ctaText}
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {HERO.heroNote && (
              <div className={styles['hero-note']}>{HERO.heroNote}</div>
            )}
          </div>
        </div>
        {/* 右侧图片视觉区域 */}
        <div className={styles['hero-visual']}>
          <div className={styles['image-frame']}>
            <div className={styles['image-card']}>
              <img
                src={HERO.heroImage}
                alt={HERO.heroImageAlt || 'Ecommerce fulfillment dashboard'}
                loading="eager"
              />
            </div>
          </div>
          {/* stat-card */}
          {(HERO.statCardLabel || HERO.statCardDesc) && (
            <div className={styles['stat-card']}>
              <b>{HERO.statCardLabel}</b>
              <span>{HERO.statCardDesc}</span>
            </div>
          )}
          {/* float-card */}
          {(HERO.floatCardTitle || HERO.floatCardDesc) && (
            <div className={styles['float-card']}>
              <h3>{HERO.floatCardTitle}</h3>
              <div className={styles['progress']}>
                <span />
              </div>
              <p>{HERO.floatCardDesc}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ========== CompareSection ==========

/**
 * CJ vs 竞品对比卡片区域（5张）
 * 【注意】卡片点击跳转站内对应子页面（href 在 constants.js 中配置）
 * featured 第一张卡片有渐变描边 border
 */
function CompareSection() {
  const { COMPARE } = useContext(ConstantsContext);
  if (!COMPARE) return null;
  return (
    <section className={styles['section-pad']} id="compare-platforms">
      <div className={styles['container']}>
        <div className={styles['header']}>
          <Kicker text={COMPARE.kicker} styles={styles} />
          <h2
            className={styles['section-title']}
            dangerouslySetInnerHTML={{ __html: COMPARE.title }}
          />
          <p className={styles['section-desc']}>{COMPARE.desc}</p>
        </div>
        <div className={styles['compare-grid']}>
          {(COMPARE.cards || []).map(function (card, i) {
            return (
              <a
                key={i}
                className={[styles['compare-card'], card.featured ? styles['featured'] : ''].filter(Boolean).join(' ')}
                href={card.href}
              >
                <div className={styles['compare-img']}>
                  {/* 【纠正】原版 compare-card 有 badge 元素，之前误删，补充还原 */}
                  <span className={styles['badge']}>Compare</span>
                  <div className={styles['logo-card']}>
                    <span className={styles['logo-pill'] + ' ' + styles['cj']}>{card.logo1}</span>
                    <span className={styles['logo-vs']}>vs</span>
                    <span
                      className={styles['logo-pill']}
                      style={card.logo2Color ? { color: card.logo2Color } : {}}
                    >
                      {card.logo2}
                    </span>
                  </div>
                </div>
                <div className={styles['compare-content']}>
                  <h3>{card.title}</h3>
                  <span className={styles['read']}>{COMPARE.readMoreText || 'Read comparison →'}</span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ========== MainFaqSection ==========

/**
 * 主页专属 FAQ 区域（左图 + info-grid + 右侧 accordion）
 * 【注意】布局与子页面 VsFaqSection（左侧摘要卡片 + accordion）完全不同，独立实现。
 * 使用 React state 控制 accordion 展开/折叠（替代 HTML <details>，避免 SSR hydration 差异）。
 * 默认第一条 FAQ 展开。
 */
function MainFaqSection({ onCtaClick }) {
  const { FAQ } = useContext(ConstantsContext);
  const [openItems, setOpenItems] = useState([0]);

  const toggleItem = useCallback(function (index) {
    setOpenItems(function (prev) {
      if (prev.indexOf(index) > -1) {
        return prev.filter(function (i) { return i !== index; });
      }
      return prev.concat([index]);
    });
  }, []);

  if (!FAQ) return null;

  return (
    <section className={styles['faq']}>
      <div className={styles['container']}>
        <div className={styles['header']}>
          <Kicker text={FAQ.kicker} styles={styles} />
          <h2
            className={styles['section-title']}
            dangerouslySetInnerHTML={{ __html: FAQ.title }}
          />
          <p className={styles['section-desc']}>{FAQ.desc}</p>
        </div>
        <div className={styles['faq-main']}>
          {/* 左侧：图片 + info-grid */}
          <div className={styles['faq-left']}>
            <div className={styles['faq-img']}>
              <img
                src={FAQ.imageUrl}
                alt={FAQ.imageAlt || 'FAQ'}
                loading="lazy"
              />
              {(FAQ.overlayTitle || FAQ.overlayDesc) && (
                <div className={styles['faq-overlay']}>
                  {FAQ.overlayTitle && <h3>{FAQ.overlayTitle}</h3>}
                  {FAQ.overlayDesc && <p>{FAQ.overlayDesc}</p>}
                </div>
              )}
            </div>
            {FAQ.infoCards && FAQ.infoCards.length > 0 && (
              <div className={styles['info-grid']}>
                {FAQ.infoCards.map(function (card, i) {
                  return (
                    <div key={i} className={styles['info-card']}>
                      <h4>{card.title}</h4>
                      <p>{card.desc}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 右侧：accordion FAQ list */}
          <div>
            <div className={styles['faq-list']}>
              {(FAQ.items || []).map(function (item, i) {
                const isOpen = openItems.indexOf(i) > -1;
                return (
                  <div
                    key={i}
                    className={[
                      styles['faq-item'],
                      isOpen ? styles['faq-item-open'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <button
                      className={styles['faq-summary']}
                      onClick={function () { toggleItem(i); }}
                    >
                      <span className={styles['qnum']}>{`Q${i + 1}`}</span>
                      <span className={styles['faq-question-text']}>{item.q}</span>
                      <span className={[styles['plus'], isOpen ? styles['plus-open'] : ''].filter(Boolean).join(' ')} />
                    </button>
                    {isOpen && (
                      <div className={styles['answer']}>
                        <p>{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {(FAQ.footerCtaText || FAQ.footerCtaBtn) && (
              <div className={styles['footer-cta']}>
                {FAQ.footerCtaText && <p>{FAQ.footerCtaText}</p>}
                {FAQ.footerCtaBtn && (
                  <button
                    className={styles['btn']}
                    onClick={function () {
                      if (onCtaClick) onCtaClick(FAQ.footerCtaUrl || '/home');
                    }}
                  >
                    {FAQ.footerCtaBtn}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 页面入口组件 ==========

/**
 * 主页入口：/alternatives
 * 【注意】遵循 odm/index.jsx 模式：
 *   - pageData.cdnHost 从 fetchData 传入，CSR fallback 读全局 CDN_HOST
 *   - constants 通过 useMemo 缓存，只在首次渲染时调用 getAlternativesConstants()
 *   - withSSR HOC 包装导出
 */
function AlternativesPage(props) {
  const pageData = props.pageData || {};
  const cdnHost = pageData.cdnHost || (typeof CDN_HOST !== 'undefined' ? CDN_HOST : '');
  const constants = useMemo(function () {
    return getAlternativesConstants(cdnHost);
  }, [cdnHost]);

  const [showModal, setShowModal] = useState(false);

  const onCtaClick = useCallback(function (url) {
    // #explore-cj-service → 锚点滚动，无需登录判断
    if (url && url.startsWith('#')) {
      if (typeof window !== 'undefined') {
        const el = document.getElementById(url.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    handleCtaClick(url, setShowModal);
  }, []);

  return (
    <CdnHostContext.Provider value={cdnHost}>
      <ConstantsContext.Provider value={constants}>
        <div className={styles['alternatives-page'] + ' ' + styles['page-root']}>
          <AlternativesHero onCtaClick={onCtaClick} />
          <SellpointsBar styles={styles} />
          <CompareSection />
          <FitSection styles={styles} />
          <AdvantagesSection styles={styles} onCtaClick={onCtaClick} />
          <TestimonialSection styles={styles} />
          <MainFaqSection onCtaClick={onCtaClick} />
          <BottomCta styles={styles} onCtaClick={onCtaClick} />
        </div>
      </ConstantsContext.Provider>
    </CdnHostContext.Provider>
  );
}

AlternativesPage.fetch = fetchData;

export default withSSR(AlternativesPage);
